/**
 * 提醒中心页 —— **H 域 H2 站内兜底通道**（+ H3 偏好的**只读**展示）
 *
 * 依据：原型 `docs/03-UX与UI设计/09-提醒中心_原型.html`（顶部计数卡 + 三档分组 + 强度/免打扰 +
 * 通道卡）· H 域 PRD **§5.2.2**（**站内不依赖任何授权、始终可用**）· **§5.1.4**（三档分组）·
 * **§5.3**（偏好只读 · 🔴 **写入口在 N 域设置页**）· **§6.1 E5**（关闭 ≠ 删除历史）。
 *
 * ## 🔴 端侧零口径（本页一个判定都不做）
 *
 * | 判定 | 归属 |
 * |---|---|
 * | 状态 / 分组 / 关键-常规 / 合并 / 免打扰是否生效 | 🔴 **服务端**（共享层 `nucleus/reminder.ts`） |
 * | 中文文案（组名 / 标题 / 描述 / 按钮） | `features/reminder-labels.ts` |
 * | 渲染顺序 / 合并条展开 | `features/reminder.ts` |
 *
 * ## 🔴 本页**不做**什么（诚实登记 · 铁律 46）
 *
 * - **不做推送授权的引导弹窗**：微信订阅消息本批**未接入**（小程序端空壳）⇒ 通道卡**如实标注
 *   「未接入」**，不画"点一下就能开推送"的假按钮；
 * - **不提供偏好写入口**：强度三档 / 五类开关 / 免打扰时段的**写路径在 N 域设置页**
 *   （`DR-031` / `DR-020` §5）⇒ 本页只**只读展示当前值**，并指路设置页；
 * - **不做真实跳转**：去还款 / 看账单明细需要对应页面（未接）⇒ 动作按钮的唯一终点是
 *   **标记已处理**（不画点了没反应的控件）。
 *
 * ## ⚠️ 语义标题用 MUI `variant`（渲染成 `h2`–`h6`）
 *
 * 铁律 41：**不手写 `<h3>`** 去凑吸顶导航；`AnchorBar` 的选择器已放宽到 `h2`–`h6`。
 */

import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Card, CardContent, Chip, Divider, Stack, Typography } from '@mui/material';
import { ReminderPolicy, type ReminderView } from '@jiaying/shared';
import { getReminders, markReminderDone, markRemindersRead } from '../api/endpoints.js';
import { AnchorBar } from '../ui/AnchorBar.js';
import { Loading } from '../ui/Loading.js';
import { ReminderItem } from '../ui/ReminderItem.js';
import {
  CHANNEL_INAPP,
  CHANNEL_INAPP_BADGE,
  CHANNEL_INAPP_NOTE,
  CHANNEL_TITLE,
  CHANNEL_WECHAT,
  CHANNEL_WECHAT_BADGE,
  CHANNEL_WECHAT_NOTE,
  DND_ACTIVE_NOTE,
  DND_IDLE_NOTE,
  DND_TITLE,
  DONE_FAILED,
  EMPTY_EARLIER_TEXT,
  EMPTY_TEXT,
  GROUP_LABELS,
  KEY_SUFFIX,
  LEVEL_LABELS,
  LEVEL_READONLY_NOTE,
  LEVEL_TITLE,
  MARK_ALL_READ,
  NORMAL_SUFFIX,
  PAGE_SUBTITLE,
  PAGE_TITLE,
  PENDING_TITLE,
  PENDING_UNIT,
  READ_FAILED,
  earlierTruncatedNote,
} from '../features/reminder-labels.js';
import { emptyKind, itemsOfGroup, storageIdsOf, summariesFor } from '../features/reminder.js';

export function ReminderPage() {
  const contentRef = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const [includeEarlier, setIncludeEarlier] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['reminders', includeEarlier],
    queryFn: () => getReminders({ includeEarlier }),
  });

  /** 统一收尾：刷新列表 + 清忙态 + 记错误（**不静默失败**） */
  const run = async (label: string, fn: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: ['reminders'] });
    } catch (err: unknown) {
      setError(`${label}${String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const onMarkRead = (item: ReminderView): void => {
    // 🔴 **合并条要展开**提交（`features/reminder.ts` 的 `storageIdsOf`）——
    //    只提交代表 id 会让同组的其余行一直是未读，下次刷新又冒出来
    void run(READ_FAILED, () => markRemindersRead({ ids: storageIdsOf(item) }));
  };
  const onDone = (item: ReminderView): void => {
    void run(DONE_FAILED, () => markReminderDone(item.id));
  };
  const onMarkAll = (): void => {
    void run(READ_FAILED, () => markRemindersRead({ all: true }));
  };

  if (q.isPending) return <Loading py={3} />;
  if (q.isError) return <Alert severity="error">提醒读取失败：{String(q.error)}</Alert>;

  const list = q.data;
  const currency = list.baseCurrency;
  const summaries = summariesFor(list, includeEarlier);
  const empty = emptyKind(list, includeEarlier);
  // 🔴 用**服务端下发的** `unreadCount` 判禁用（端侧不重数 —— 重数 = 第二份口径）
  const anyUnread = list.unreadCount > 0;

  return (
    <Stack spacing={3} ref={contentRef}>
      <AnchorBar contentRef={contentRef} />

      <Box>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {PAGE_TITLE}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {PAGE_SUBTITLE}
        </Typography>
      </Box>

      {error === null ? null : <Alert severity="error">{error}</Alert>}

      {/* ─────────────────── 顶部：待处理计数 + 全部标记已读 ─────────────────── */}
      <Card data-testid="reminder-summary">
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" component="div">
              {PENDING_TITLE}
            </Typography>
            <Typography variant="h4" data-testid="reminder-unread">
              {list.unreadCount}
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                {PENDING_UNIT}
              </Typography>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {list.keyCount} {KEY_SUFFIX.replace('条', '')} · {list.normalCount} {NORMAL_SUFFIX.replace('条', '')}
            </Typography>
            <Box sx={{ flex: 1 }} />
            {/* 🔴 没有未读 ⇒ **禁用**（而不是点了没反应 —— 项目禁"点不动的控件"） */}
            <Button size="small" variant="contained" disabled={busy || !anyUnread} onClick={onMarkAll} data-testid="reminder-mark-all">
              {MARK_ALL_READ}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* ─────────────────── 强度 / 免打扰（**只读**） ─────────────────── */}
      <Card data-testid="reminder-prefs">
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {LEVEL_TITLE}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
            <Chip size="small" color="primary" label={LEVEL_LABELS[list.level]} data-testid="reminder-level" />
            <Typography variant="body2" color="text.secondary">
              {LEVEL_READONLY_NOTE}
            </Typography>
          </Stack>

          <Divider sx={{ my: 1.5 }} />

          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {DND_TITLE}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} data-testid="reminder-dnd">
            {list.dnd.enabled ? `${list.dnd.start} – ${list.dnd.end} · ` : ''}
            {list.dnd.active ? DND_ACTIVE_NOTE : DND_IDLE_NOTE}
          </Typography>
        </CardContent>
      </Card>

      {/* ─────────────────── 通道（🔴 如实呈现） ─────────────────── */}
      <Card data-testid="reminder-channels">
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {CHANNEL_TITLE}
          </Typography>
          <Stack spacing={1} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {CHANNEL_INAPP}
              </Typography>
              <Chip size="small" color="success" label={CHANNEL_INAPP_BADGE} />
              <Typography variant="caption" color="text.secondary">
                {CHANNEL_INAPP_NOTE}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }} color="text.secondary">
                {CHANNEL_WECHAT}
              </Typography>
              <Chip size="small" variant="outlined" label={CHANNEL_WECHAT_BADGE} />
              <Typography variant="caption" color="text.secondary">
                {CHANNEL_WECHAT_NOTE}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* ─────────────────── 三档分组 ─────────────────── */}
      {empty === 'none' ? (
        <Card data-testid="reminder-empty">
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              {EMPTY_TEXT}
            </Typography>
          </CardContent>
        </Card>
      ) : null}

      {summaries.map((s) => {
        const items = itemsOfGroup(list.items, s.key);
        const title = GROUP_LABELS[s.key];
        return (
          <Box component="section" key={s.key} data-anchor={title} data-testid={`group-${s.key}`}>
            <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1 }}>
              {/* 🔴 MUI 语义标题（渲染成 h6）—— 不手写 <h3> 去凑 AnchorBar（铁律 41） */}
              <Typography variant="h6">{title}</Typography>
              <Typography variant="caption" color="text.secondary" data-testid={`count-${s.key}`}>
                {s.count}
              </Typography>
              {s.key === 'earlier' ? (
                <Button size="small" onClick={() => { setIncludeEarlier((v) => !v); }} data-testid="toggle-earlier">
                  {includeEarlier ? '隐藏历史' : '显示历史'}
                </Button>
              ) : null}
            </Stack>

            {items.length === 0 ? (
              <Typography variant="body2" color="text.secondary" data-testid={`empty-${s.key}`}>
                {s.key === 'earlier' ? EMPTY_EARLIER_TEXT : EMPTY_TEXT}
              </Typography>
            ) : (
              <Card>
                <CardContent>
                  <Stack divider={<Divider flexItem />}>
                    {items.map((it) => (
                      <ReminderItem
                        key={it.id}
                        item={it}
                        currency={currency}
                        pending={busy}
                        onMarkRead={onMarkRead}
                        onDone={onDone}
                      />
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Box>
        );
      })}

      {/* 🔴 截断必须说出口（静默截断 = 让用户以为历史只有这么多） */}
      {list.earlierTruncated ? (
        <Alert severity="info" data-testid="reminder-truncated">
          {earlierTruncatedNote(ReminderPolicy.EarlierLimit)}
        </Alert>
      ) : null}
    </Stack>
  );
}
